import React, { useState, useEffect } from 'react';
import { API_BASE_URL, API_URL } from "../config";
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  FaArrowLeft, FaCheckCircle, FaTimes, FaClock, 
  FaUsers, FaTruck, FaWeightHanging, FaMapMarkerAlt,
  FaMoneyBillWave, FaCalendarAlt, FaSpinner,
  FaInfoCircle, FaGavel, FaPlus, FaTrash,
  FaEdit, FaEye, FaCheck, FaPaperPlane
} from 'react-icons/fa';
import AdminLayout from '../layouts/AdminLayout';

function TenderDetail({ onLogout }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tender, setTender] = useState(null);
  const [bids, setBids] = useState([]);
  const [user, setUser] = useState({});
  const [processing, setProcessing] = useState(false);
  const [showAwardModal, setShowAwardModal] = useState(false);
  const [selectedBids, setSelectedBids] = useState([]);
  const [showCounterModal, setShowCounterModal] = useState(false);
  const [counterBidId, setCounterBidId] = useState(null);
  const [counterRate, setCounterRate] = useState('');

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    fetchTenderDetails();
  }, [id]);

  const fetchTenderDetails = async () => {
    setLoading(true);
    try {
      const [tenderRes, bidsRes] = await Promise.all([
        fetch(`/tenders/${id}`),
        fetch(`/tenders/${id}/bids`)
      ]);

      const tenderData = await tenderRes.json();
      const bidsData = await bidsRes.json();

      if (tenderData.success) {
        setTender(tenderData.tender);
      }
      if (bidsData.success) {
        setBids(bidsData.bids || []);
      }
    } catch (err) {
      console.error('Error fetching tender details:', err);
      toast.error('Failed to load tender details');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectBid = (bidId) => {
    setSelectedBids(prev => {
      if (prev.includes(bidId)) {
        return prev.filter(id => id !== bidId);
      }
      return [...prev, bidId];
    });
  };

  const handleAwardTender = async () => {
    if (selectedBids.length === 0) {
      toast.error('Please select at least one bid to award');
      return;
    }

    const selectedBidData = bids.filter(b => selectedBids.includes(b.id));
    const awards = selectedBidData.map(bid => {
      const volume = tender.total_volume_mt / selectedBidData.length;
      return {
        subcontractor_id: bid.subcontractor_id,
        bid_id: bid.id,
        awarded_volume_mt: volume,
        awarded_rate: bid.bid_rate
      };
    });

    setProcessing(true);
    try {
      const response = await fetch(`/tenders/${id}/award`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ awards })
      });
      const data = await response.json();
      if (data.success) {
        toast.success(`✅ Tender awarded to ${selectedBids.length} subcontractor(s)!`);
        setShowAwardModal(false);
        setSelectedBids([]);
        fetchTenderDetails();
      } else {
        toast.error(data.error || 'Failed to award tender');
      }
    } catch (err) {
      console.error('Error awarding tender:', err);
      toast.error('Failed to award tender');
    } finally {
      setProcessing(false);
    }
  };

  const handleCounterOffer = async () => {
    if (!counterRate || parseFloat(counterRate) <= 0) {
      toast.error('Please enter a valid rate');
      return;
    }

    const bid = bids.find(b => b.id === counterBidId);
    if (!bid) return;

    setProcessing(true);
    try {
      const response = await fetch(`/tenders/${id}/counter-offer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subcontractor_id: bid.subcontractor_id,
          counter_rate: parseFloat(counterRate),
          notes: `Counter offer from admin`
        })
      });
      const data = await response.json();
      if (data.success) {
        toast.success(`📤 Counter offer sent to ${bid.subcontractor?.company_name || 'subcontractor'}`);
        setShowCounterModal(false);
        setCounterRate('');
        setCounterBidId(null);
      } else {
        toast.error(data.error || 'Failed to send counter offer');
      }
    } catch (err) {
      console.error('Error sending counter offer:', err);
      toast.error('Failed to send counter offer');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      'open': 'bg-green-100 text-green-600',
      'closed': 'bg-yellow-100 text-yellow-600',
      'awarded': 'bg-blue-100 text-blue-600',
      'cancelled': 'bg-red-100 text-red-600',
      'submitted': 'bg-blue-100 text-blue-600',
      'withdrawn': 'bg-gray-100 text-gray-600',
      'shortlisted': 'bg-purple-100 text-purple-600',
      'rejected': 'bg-red-100 text-red-600'
    };
    return `px-2 py-1 rounded-full text-xs font-semibold ${styles[status] || 'bg-gray-100 text-gray-600'}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-ZA', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <AdminLayout activeTab="tenders" onLogout={onLogout} user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <FaSpinner className="animate-spin text-4xl text-green-500 mx-auto mb-4" />
            <p className="text-gray-500">Loading tender details...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!tender) {
    return (
      <AdminLayout activeTab="tenders" onLogout={onLogout} user={user}>
        <div className="bg-white rounded-xl p-8 text-center border" style={{ borderColor: '#E8ECEF' }}>
          <div className="text-4xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-red-600 mb-2">Tender Not Found</h2>
          <p className="text-gray-500">The tender you're looking for doesn't exist.</p>
          <Link to="/tender-dashboard" className="mt-4 inline-block px-4 py-2 rounded-lg text-white" style={{ backgroundColor: '#00A86B' }}>
            Back to Dashboard
          </Link>
        </div>
      </AdminLayout>
    );
  }

  const job = tender.jobs;

  return (
    <AdminLayout activeTab="tenders" onLogout={onLogout} user={user}>
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Link to="/tender-dashboard" className="p-2 rounded-lg hover:bg-gray-100 transition-colors" style={{ color: '#0A1929' }}>
          <FaArrowLeft size={20} />
        </Link>
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#0A1929' }}>
            {tender.tender_number}
            <span className={`ml-3 ${getStatusBadge(tender.status)}`}>
              {tender.status}
            </span>
            {tender.tender_type === 'framework' && (
              <span className="ml-2 px-2 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-600">
                Framework
              </span>
            )}
          </h1>
          <p className="text-sm" style={{ color: '#4A5568' }}>
            {job?.commodity || 'Unknown'} • {tender.total_volume_mt || 0} MT • {job?.origin} → {job?.destination}
          </p>
        </div>
      </div>

      {/* Tender Info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl border p-4" style={{ borderColor: '#E8ECEF' }}>
          <p className="text-xs text-gray-500">Deadline</p>
          <p className="font-medium text-navy">{formatDate(tender.bidding_deadline)}</p>
        </div>
        <div className="bg-white rounded-xl border p-4" style={{ borderColor: '#E8ECEF' }}>
          <p className="text-xs text-gray-500">Target Rate</p>
          <p className="font-medium text-navy">R{tender.target_rate || 'N/A'}/MT</p>
        </div>
        <div className="bg-white rounded-xl border p-4" style={{ borderColor: '#E8ECEF' }}>
          <p className="text-xs text-gray-500">Winners</p>
          <p className="font-medium text-navy">{tender.number_of_winners}</p>
        </div>
      </div>

      {/* Bids Section */}
      <div className="bg-white rounded-xl border p-6" style={{ borderColor: '#E8ECEF' }}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-navy">
            <FaUsers className="inline mr-2" style={{ color: '#00A86B' }} />
            Bids ({bids.length})
          </h2>
          {tender.status === 'open' && bids.length > 0 && (
            <button
              onClick={() => setShowAwardModal(true)}
              className="px-4 py-2 bg-green text-white rounded-lg text-sm hover:bg-green transition-colors flex items-center gap-2"
            >
              <FaCheck size={14} /> Award Selected
            </button>
          )}
        </div>

        {bids.length === 0 ? (
          <p className="text-center py-8 text-gray-500">No bids submitted yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  {tender.status === 'open' && (
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Select</th>
                  )}
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Subcontractor</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Rate (R/MT)</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Trucks</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Capacity</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Status</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {bids.map((bid) => (
                  <tr key={bid.id} className="border-b border-gray-100 hover:bg-gray-50">
                    {tender.status === 'open' && (
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedBids.includes(bid.id)}
                          onChange={() => handleSelectBid(bid.id)}
                          className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                          disabled={bid.status === 'withdrawn'}
                        />
                      </td>
                    )}
                    <td className="px-4 py-3 text-sm font-medium text-navy">
                      {bid.subcontractor?.company_name || 'Unknown'}
                    </td>
                    <td className="px-4 py-3 text-sm font-bold text-blue-600">
                      R{bid.bid_rate}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {bid.trucks_offered}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {bid.monthly_capacity_mt ? `${bid.monthly_capacity_mt} MT/mo` : 'N/A'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={getStatusBadge(bid.status)}>
                        {bid.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => {
                          setCounterBidId(bid.id);
                          setShowCounterModal(true);
                        }}
                        className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                        disabled={bid.status === 'withdrawn'}
                      >
                        <FaPaperPlane size={12} /> Counter
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Award Modal */}
      {showAwardModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowAwardModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6">
            <h2 className="text-xl font-bold text-navy mb-4">Confirm Award</h2>
            <p className="text-sm text-gray-600 mb-4">
              Award the tender to <strong>{selectedBids.length}</strong> selected subcontractor(s)?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowAwardModal(false)}
                className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAwardTender}
                disabled={processing}
                className="px-4 py-2 bg-green text-white rounded-lg text-sm hover:bg-green transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {processing ? <FaSpinner className="animate-spin" /> : <FaCheck />}
                {processing ? 'Processing...' : 'Confirm Award'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Counter Offer Modal */}
      {showCounterModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowCounterModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6">
            <h2 className="text-xl font-bold text-navy mb-4">Send Counter Offer</h2>
            <p className="text-sm text-gray-600 mb-4">
              Enter a counter offer rate for this subcontractor.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Counter Rate (R/MT)</label>
              <input
                type="number"
                step="0.01"
                value={counterRate}
                onChange={(e) => setCounterRate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                placeholder="Enter rate"
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowCounterModal(false)}
                className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCounterOffer}
                disabled={processing}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {processing ? <FaSpinner className="animate-spin" /> : <FaPaperPlane />}
                {processing ? 'Sending...' : 'Send Counter Offer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default TenderDetail;
